// @ts-strict-ignore
import * as React from 'react';
import {PureComponent} from 'react';
import type {GradientColorObject, PatternObject, Point} from 'highcharts';
import isNumber from 'lodash/isNumber';
import clsx from 'clsx';

import {withStyles} from 'common/util/react';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import type {FormattedRow, LiftRow, RawCell} from 'common/report/retention/util/types';
import {CurrentProject} from 'common/report/model-resources/projects';
import {
  getLiftBaselineSegmentRawHead,
  getRetentionCurveTableColumnGroups,
  mapToRetentionCurveTableRows,
} from 'common/report/retention/util/parsing';
import {IronContextRegistry} from 'common/report/context-registry';
import {DEFAULT_CHART_WIDTH, REPORT_LIMITS, ReportName} from 'common/report/constants';
import {ChartType} from 'common/types/reports/bookmark';
import {IChartDataItem, LineChartYAxisOptions, Popover} from 'common/charts/visualization/line-chart/types';
import {XAxisUnit} from 'common/charts/visualization/line-chart/enums';
import {MultimetricData} from 'common/report/insights/charts/retention-curve-chart/types';
import {deselectPoint, selectPoint} from 'common/report/util/highcharts';
import {handlePopoverAction, RetentionPopover} from 'common/report/retention/visualization/popover';
import {AGGREGATED_ROW_NAME} from 'common/report/retention/util/constants';
import {RetentionBookmarkController} from 'common/report/retention/bookmark-controller';
import {ButtonOption} from 'common/components/popover-card/types';
import {LineChartReact} from 'common/charts/visualization/line-chart';
import {NoDataHeroReact} from 'common/widgets/warning-hero/no-data-hero';
import {WarningHeroReportName} from 'common/widgets/warning-hero/constants';
import {SegmentLegendHeader} from 'common/charts/misc/segment-legend-header';
import {ActionsPopoverReact} from 'common/widgets/actions-popover';
import {PopoverBodyReact} from 'common/report/retention/visualization/popoverBody';
import {DisplayAnalysisType} from 'common/charts/visualization/line-chart/enums';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {useTranslation} from 'common/i18n/hooks';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useReportLayoutManager} from 'common/contexts/react/report-layout-manager-context';
import {useSegmentFilterController} from 'common/contexts/react/segment-filter-controller-context';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';

import styles from './index.module.styl';
import {getDataCellRenderer} from './template';
import {computeTicks, getLineChartTooltip} from './line-chart-format';
import css from './index.module.styl?inline';

type PopoverParams = {
  popover: RetentionPopover;
  title: string;
  subtitle: string;
  buttonOptions: ButtonOption[];
  preventCloseSelectors: string[];
  color: string | GradientColorObject | PatternObject;
  isAggregate: boolean;
  retainedCount: string;
  dataGroupName: string;
  dataGroupNamePlural: string;
  retainedPercent: string;
  dropOffCount: string;
  dropOffPercent: string;
  propertyAggregation: string;
};

export type RetentionCurveChartState = {
  chartWidth: number; // px
  truncatedSegmentDisplayLookup?: Map<string, string>;
  selectedHighchartsPoint?: Point;
  popoverOpen: boolean;
  popoverParams: PopoverParams;
  popoverStyles?: {top: string; left: string};
};

export interface RetentionCurveChartProps {
  bookmarkData: MultimetricData;
  limitDisplayedSegments?: boolean;
  popoverProp?: () => Popover;
  projectTimezone?: string;
  segmentDisplayLimit?: number;
  segmentFilterController?: SegmentFilterController;
  showPopover?: boolean;
  utcOffset?: number;
}

export type RetentionCurveChartJsxProps = {
  props: RetentionCurveChartProps;
};

class RetentionCurveChart extends PureComponent<
  RetentionCurveChartProps & Pick<IronContextRegistry, `currentProject` | `reportLayoutManager` | `i18n` | `metrics`>,
  RetentionCurveChartState
> {
  public declare popoverProp?: () => Popover;

  formatTicksX = computeTicks();

  private declare _bookmarkController: RetentionBookmarkController;

  declare static defaultProps: Partial<RetentionCurveChartProps>;

  constructor(props) {
    super(props);
    this.state = {
      popoverOpen: false,
      popoverParams: null,
      chartWidth: DEFAULT_CHART_WIDTH, // px
    };
  }

  get bookmarkController() {
    const data = this.props.bookmarkData;
    if (!data) {
      throw new Error(`Missing bookmarkData`);
    }

    if (this._bookmarkController && this._bookmarkController.isEquivalent(data.bookmark, data.queryResults)) {
      return this._bookmarkController;
    }
    this._bookmarkController = new RetentionBookmarkController(data.bookmark, data.queryResults);
    requestAnimationFrame(() => this.updateCurveChartData());
    return this._bookmarkController;
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.props.segmentFilterController;
  }

  get selectedSegmentsRows() {
    const {chartRows} = this.getCurveChartData();
    return chartRows.filter((row) => this.segmentFilterController.isItemSelected(0, row.raw_head));
  }

  get selectedSegmentLiftRows() {
    const {formattedLiftRowsData} = this.getCurveChartData();
    return this.selectedSegmentsRows.map((row) => formattedLiftRowsData.get(row.raw_head));
  }

  private getPopoverParams({
    selectedRow,
    colIdx,
    rowIdx,
    color,
  }: {
    selectedRow: FormattedRow<RawCell>;
    colIdx: number;
    rowIdx: number;
    color: string | GradientColorObject | PatternObject;
  }) {
    const matchingShowClauseIndex = selectedRow.matching_show_clause_index;
    const retentionType = this.bookmarkController.getRetentionType(matchingShowClauseIndex);
    const isSegmented = this.bookmarkController.getSegments().length > 0;
    const isAggregated = isSegmented || selectedRow.raw_head === AGGREGATED_ROW_NAME(retentionType);
    const cell = selectedRow.data[colIdx];

    const formatter = this.bookmarkController.popoverFormatter(this.props.metrics, matchingShowClauseIndex);

    const enteredValue =
      this.bookmarkController.hasMultipleRetentionMetrics() && isSegmented
        ? cell.entered_value.split(` / `).slice(1).join(` / `)
        : cell.entered_value;
    const popover = this.bookmarkController.createPopover({
      matchingShowClauseIndex,
      projectTimezone: this.props.projectTimezone,
      colIdx,
      rowIdx,
      absoluteValue: formatter(cell.value, false),
      absoluteDropOffValue: formatter(cell.absolute_drop_off_value, false),
      cohortSize: cell.cohort_size,
      completeDate: cell.complete_date,
      enteredValue,
      enteredDate: cell.date,
      incomplete: cell.incomplete,
      isAggregated,
      i18n: this.props.i18n,
    });

    return {
      popover,
      title: popover.getTitle(),
      subtitle: popover.getSubtitle(),
      buttonOptions: popover.getButtonOptions(),
      preventCloseSelectors: [`.bar.hoverable`],
      color,
      isAggregate: popover.isAggregatedCell(),
      retainedCount: popover.getRetainedCount(),
      dataGroupName: popover.getDataGroupName(),
      dataGroupNamePlural: popover.getDataGroupNamePlural(),
      retainedPercent: formatter(cell.percent, true),
      dropOffCount: popover.getDropOffCount(),
      dropOffPercent: formatter(cell.percent_drop_off_value, true),
      propertyAggregation: popover.getPropertyAggregation(),
    };
  }

  handlePopoverActionClick = (ev) => {
    const {action} = ev.detail;
    handlePopoverAction({
      action,
      color: this.state.popoverParams.color as string,
      popover: this.state.popoverParams.popover,
      updateUserSidebarContextFn: (isOpen: boolean) => this.props.reportLayoutManager.updateIsUserSidebarOpen(isOpen),
    });
  };

  private hasRequiredParams(): boolean {
    return Boolean(this.bookmarkController);
  }

  private updateCurveChartData() {
    if (this.hasRequiredParams()) {
      this.processData();
    }
  }

  liftDataForSegment(formattedRow: FormattedRow): {liftRow: LiftRow; baselineRow?: FormattedRow<RawCell>} {
    if (this.bookmarkController.getLiftComparison()?.groupValue) {
      const {chartRows, formattedLiftRowsData, segmentKeyToPath} = this.getCurveChartData();
      const baselineRowKey = getLiftBaselineSegmentRawHead(
        formattedRow,
        segmentKeyToPath,
        this.bookmarkController.getLiftComparison(),
      );
      const baselineRow = chartRows.find((row) => row.raw_head === baselineRowKey);
      const liftRow = formattedLiftRowsData.get(formattedRow.raw_head);
      return {
        baselineRow,
        liftRow,
      };
    }
    return null;
  }

  private getCurveChartData() {
    const timezone = this.getProjectTimezone();
    const {chartRows, formattedLiftRowsData, segmentKeyToPath} = this.bookmarkController.getCurveChartData(
      timezone,
      this.props.i18n,
    );
    return {chartRows, formattedLiftRowsData, segmentKeyToPath};
  }

  processData() {
    const {chartRows, formattedLiftRowsData, segmentKeyToPath} = this.getCurveChartData();

    const tableRows = mapToRetentionCurveTableRows({
      formattedUiRows: chartRows,
      bookmarkController: this.bookmarkController,
      segmentKeyToPath,
      formattedLiftRowsData,
    });
    const tableRowLookup = new Map(tableRows.map((row) => [row.identifier, row]));

    const segmentFilterItems = chartRows.map((row) => ({
      label: row.display_head,
      id: row.raw_head,
      tableRow: tableRowLookup.get(row.raw_head),
    }));

    const tableColumns = getRetentionCurveTableColumnGroups({
      formattedUiRows: chartRows,
      bookmarkController: this.bookmarkController,
      metricsResource: this.props.metrics,
      i18n: this.props.i18n,
      getDataCellRenderer,
    });

    const maxNumSelections =
      this.props.segmentDisplayLimit > 0
        ? this.props.segmentDisplayLimit
        : REPORT_LIMITS[ReportName.Retention].segments[ChartType.RetentionTable].max;
    // Careful, this will trigger a re-render
    this.segmentFilterController.loadSections([{items: segmentFilterItems, tableColumns}], {
      defaultNumSelections: REPORT_LIMITS[ReportName.Retention].segments[ChartType.RetentionTable].default,
      maxNumSelections,
      tableSortConfig: this.bookmarkController.getSorting(),
    });
    this.segmentFilterController.loadFromSavedLegend(
      this.bookmarkController.getLegendSelectedSegments() && [
        {selected: this.bookmarkController.getLegendSelectedSegments().map((key) => ({key}))},
      ],
    );
    this.segmentFilterController.segmentationQueryLimit = REPORT_LIMITS[ReportName.Retention].query;
    this.segmentFilterController.exportSegmentationQueryLimit = REPORT_LIMITS[ReportName.Retention].csvExport;
  }

  getCurrentProject(): CurrentProject {
    return this.props.currentProject.get().value;
  }

  componentDidMount() {
    //TODO add store fetch
    this.updateCurveChartData();
  }

  onResize(width: number) {
    this.setState({
      chartWidth: width,
    });
  }

  hasResults = () => this.bookmarkController.hasResults();
  getChartType = () => this.bookmarkController.getChartType();
  isFormattingTicksX = () => this.state.chartWidth > DEFAULT_CHART_WIDTH;
  getProjectTimezone = () => this.props.projectTimezone || this.getCurrentProject()?.timezone;

  getUtcOffset = () => (isNumber(this.props.utcOffset) ? this.props.utcOffset : this.getCurrentProject()?.utcOffset);

  getChartData = (): IChartDataItem[] => {
    const {chartRows, formattedLiftRowsData, segmentKeyToPath} = this.getCurveChartData();

    return chartRows
      .filter((row) => this.segmentFilterController.isItemSelected(0, row.raw_head))
      .map((row) => {
        const liftRow = formattedLiftRowsData?.get(row.raw_head);
        const matchingShowClauseIndex = row.matching_show_clause_index;
        const data = (liftRow || row).data.map(
          (cell, idx) =>
            [idx, this.bookmarkController.isPercentageView(matchingShowClauseIndex) ? cell.percent : cell.value] as [
              number,
              number,
            ],
        );

        const path = segmentKeyToPath && segmentKeyToPath[row.raw_head] ? segmentKeyToPath[row.raw_head] : [];

        return {
          axis: this.bookmarkController.getAxisAssignment(matchingShowClauseIndex),
          id: row.raw_head,
          name: row.raw_head,
          data,
          color: this.segmentFilterController.getItemColor(0, row.raw_head),
          incompleteLength: getNumIncompleteCells(row),
          path,
        };

        function getNumIncompleteCells(r) {
          const incompleteIdx = r.data.findIndex((d) => d.incomplete);
          return incompleteIdx >= 0 ? r.data.length - incompleteIdx : 0;
        }
      });
  };

  getXAxisOptions = () => {
    const bookmarkOptions = this.bookmarkController.getXAxisOptions();
    return {...bookmarkOptions, unit: XAxisUnit.Time};
  };

  getXAxisDefaultOptions = () => {
    return this.bookmarkController.getXAxisDefaultOptions();
  };

  getPrimaryYAxisOptions = (): LineChartYAxisOptions => {
    const options: LineChartYAxisOptions = this.bookmarkController.getPrimaryYAxisOptions();
    if (this.bookmarkController.hasRetentionRateOnly()) {
      return {...options, maxPadding: 0};
    }
    return options;
  };

  getSecondaryYAxisOptions = (): LineChartYAxisOptions => {
    const options: LineChartYAxisOptions = this.bookmarkController.getSecondaryYAxisOptions();
    if (this.bookmarkController.hasRetentionRateOnly()) {
      return {...options, maxPadding: 0};
    }
    return options;
  };

  getYAxisDefaultOptions = (axis: BookmarkTypes.AxisAssignment) => {
    return this.bookmarkController.getYAxisDefaultOptions(this.props.metrics, axis);
  };

  isEndDateIncomplete = () => {
    const selectedSegmentsRows = this.selectedSegmentsRows;
    if (selectedSegmentsRows.every((row) => row.data.length < 2)) {
      return false;
    }
    for (const row of selectedSegmentsRows) {
      for (const cell of row.data) {
        if (cell.incomplete) {
          return true;
        }
      }
    }
    return false;
  };

  closePopover = () => {
    deselectPoint(this.state.selectedHighchartsPoint);
    this.setState({
      popoverOpen: false,
      selectedHighchartsPoint: null,
    });
  };

  handleHighChartClick = (point: Point) => {
    const {chartRows} = this.getCurveChartData();
    const {series, index, color} = point;
    const segmentKey = series.options.id;

    selectPoint(point);

    const left = series.chart.plotLeft + point.plotX + 12;
    const top = series.chart.plotTop + point.plotY + 12;
    const popoverStyles = {left: `${left}px`, top: `${top}px`};
    const row = chartRows.filter((r) => r.head === segmentKey);
    const selectedRow = row[0];
    const rowIdx = chartRows.indexOf(selectedRow);

    this.setState({
      selectedHighchartsPoint: point,
      popoverStyles,
      popoverOpen: this.props.showPopover,
      popoverParams: this.props.showPopover
        ? {
            ...this.getPopoverParams({
              selectedRow,
              colIdx: index,
              rowIdx,
              color,
            }),
          }
        : null,
    });
  };

  render() {
    if (!this.hasResults()) {
      return (
        <div className={`retention-curve-chart`}>
          <NoDataHeroReact reportName={WarningHeroReportName.Retention} />
        </div>
      );
    }

    return (
      <div className={styles.root}>
        {/* Remove inline css when converting to React */}
        <style>{css}</style>
        <SegmentLegendHeader
          className={styles.segmentHeader}
          segmentFilterController={this.props.segmentFilterController}
        />
        <LineChartReact
          chartData={this.getChartData()}
          className={clsx(styles.lineChart, `lineChart`)}
          displayAnalysisType={DisplayAnalysisType.Linear}
          formatTooltip={getLineChartTooltip(this, this.props.metrics, this.bookmarkController)}
          formatXAxisLabel={this.bookmarkController.formatRetentionCurveXAxisLabel()}
          formatYAxisLabel={this.bookmarkController.formatRetentionCurveYAxisLabel(this.props.metrics)}
          incompleteToDate={this.isEndDateIncomplete()}
          onHighChartClick={this.handleHighChartClick}
          onResize={(contentRect) => this.onResize(contentRect)}
          defaultPrimaryYAxisOptions={this.getYAxisDefaultOptions(BookmarkTypes.AxisAssignment.Primary)}
          defaultSecondaryYAxisOptions={this.getYAxisDefaultOptions(BookmarkTypes.AxisAssignment.Secondary)}
          defaultXAxisOptions={this.getXAxisDefaultOptions()}
          primaryYAxisOptions={this.getPrimaryYAxisOptions()}
          secondaryYAxisOptions={this.getSecondaryYAxisOptions()}
          segmentFilterController={this.segmentFilterController}
          timezone={this.getProjectTimezone()}
          utcOffset={this.getUtcOffset()}
          xAxisOptions={this.getXAxisOptions()}
          zoomType={`x`}
          {...(this.isFormattingTicksX() ? {formatTicksX: this.formatTicksX} : {})}
        />
        {this.props.showPopover && this.state.popoverOpen && this.state.popoverParams ? (
          <ActionsPopoverReact
            buttonOptions={this.state.popoverParams.buttonOptions}
            className={styles.actionsPopover}
            style={this.state.popoverStyles}
            onAction={(ev) => this.handlePopoverActionClick(ev)}
            onClose={this.closePopover}
            open={this.state.popoverOpen}
            preventCloseSelectors={this.state.popoverParams.preventCloseSelectors}
            subtitle={this.state.popoverParams.subtitle}
            title={this.state.popoverParams.title}
          >
            <PopoverBodyReact
              {...{
                isAggregate: this.state.popoverParams.isAggregate,
                retainedCount: this.state.popoverParams.retainedCount,
                dataGroupName: this.state.popoverParams.dataGroupName,
                dataGroupNamePlural: this.state.popoverParams.dataGroupNamePlural,
                retainedPercent: this.state.popoverParams.retainedPercent,
                dropOffCount: this.state.popoverParams.dropOffCount,
                dropOffPercent: this.state.popoverParams.dropOffPercent,
                propertyAggregation: this.state.popoverParams.propertyAggregation,
                cssModule: styles,
              }}
            />
          </ActionsPopoverReact>
        ) : null}
      </div>
    );
  }
}

export type {RetentionCurveChart};

export const RetentionCurveChartReact = withStyles(
  React.forwardRef<RetentionCurveChart, RetentionCurveChartProps>((props, ref) => {
    const currentProject = useCurrentProject();
    const ctxSegmentFilterController = useSegmentFilterController();
    const i18n = useTranslation();
    const metrics = useMetrics();
    const reportLayoutManager = useReportLayoutManager();
    const segmentFilterController =
      props.segmentFilterController || ctxSegmentFilterController || new SegmentFilterController();

    return (
      <RetentionCurveChart
        currentProject={currentProject}
        i18n={i18n}
        metrics={metrics}
        ref={ref}
        reportLayoutManager={reportLayoutManager}
        segmentFilterController={segmentFilterController}
        {...props}
      />
    );
  }),
  css,
);
